import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { useToast } from '../hooks/use-toast';
import {
  Users,
  Building2,
  Calendar,
  CreditCard,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Search,
  Edit,
  Plus,
  Shield,
  Crown,
  Bell,
  Megaphone,
  Filter,
  Send,
  Ban,
  RotateCcw,
  Wallet,
  Receipt,
  FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MasterControlPanel = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [admins, setAdmins] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPlan, setFilterPlan] = useState('all');
  const [dateRangeFilter, setDateRangeFilter] = useState('all');
  const [lastUpdated, setLastUpdated] = useState(null);
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [editData, setEditData] = useState({
    plan: '',
    payment_mode: '',
    extend_days: 0,
    status: '',
    start_date: '',
    expiry_date: ''
  });
  const [saving, setSaving] = useState(false);
  
  // Notification dialogs
  const [notifyDialogOpen, setNotifyDialogOpen] = useState(false);
  const [broadcastDialogOpen, setBroadcastDialogOpen] = useState(false);
  const [notificationData, setNotificationData] = useState({ title: '', message: '' });
  const [broadcastData, setBroadcastData] = useState({ title: '', message: '', target: 'all' });
  const [sendingNotification, setSendingNotification] = useState(false);

  // Get token
  const token = localStorage.getItem('token');

  // Subscription plans
  const plans = [
    { id: 'PLAN_FREE_TRIAL', name: 'Free Trial', days: 15 },
    { id: 'PLAN_1_MONTH', name: '1 Month', days: 30 },
    { id: 'PLAN_3_MONTHS', name: '3 Months', days: 90 },
    { id: 'PLAN_6_MONTHS', name: '6 Months', days: 180 },
    { id: 'PLAN_12_MONTHS', name: '12 Months', days: 365 },
    { id: 'PLAN_LIFETIME', name: 'Lifetime', days: 36500 }
  ];

  // Check access
  useEffect(() => {
    if (currentUser && currentUser.role !== 'master_panel' && !currentUser.isMaster) {
      toast({
        title: "Access Denied",
        description: "You don't have permission to access the Master Control Panel",
        variant: "destructive"
      });
      navigate('/dashboard');
    }
  }, [currentUser, navigate, toast]);

  // Fetch data
  useEffect(() => {
    if (currentUser?.role === 'master_panel' || currentUser?.isMaster) {
      fetchAdmins();
      fetchStats();
      
      // Auto-refresh every 30 seconds
      const interval = setInterval(() => {
        fetchAdmins();
        fetchStats();
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [currentUser]);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/master-panel/admins`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch admins');
      
      const data = await response.json();
      setAdmins(data.admins || []);
      setLastUpdated(new Date());
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/master-panel/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch stats');
      
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Handle edit subscription click
  const handleEditClick = (admin) => {
    setSelectedAdmin(admin);
    setEditData({
      plan: admin.subscription_plan || 'PLAN_FREE_TRIAL',
      payment_mode: admin.payment_method || 'free_trial',
      extend_days: 0,
      status: admin.subscription_status || 'active',
      start_date: admin.start_date ? admin.start_date.split('T')[0] : new Date().toISOString().split('T')[0],
      expiry_date: admin.expiry_date ? admin.expiry_date.split('T')[0] : new Date().toISOString().split('T')[0]
    });
    setEditDialogOpen(true);
  };

  // Save subscription changes
  const handleSaveSubscription = async () => {
    if (!selectedAdmin) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/master-panel/admin/${selectedAdmin.admin_id}/subscription`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editData)
      });
      
      if (!response.ok) throw new Error('Failed to update subscription');
      
      const result = await response.json();
      
      toast({
        title: "Success",
        description: result.message,
      });
      
      setEditDialogOpen(false);
      fetchAdmins();
      fetchStats();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  // Approve subscription
  const handleApprove = async (admin) => {
    try {
      const plan = plans.find(p => p.id === (admin.subscription_plan || 'PLAN_FREE_TRIAL'));
      const response = await fetch(`${API_URL}/api/master-panel/admin/${admin.admin_id}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          plan: admin.subscription_plan || 'PLAN_FREE_TRIAL',
          days: plan?.days || 30
        })
      });
      
      if (!response.ok) throw new Error('Failed to approve subscription');
      
      toast({
        title: "Success",
        description: `Subscription approved for ${admin.admin_name}`,
      });
      
      fetchAdmins();
      fetchStats();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  // Reject subscription
  const handleReject = async (admin) => {
    try {
      const response = await fetch(`${API_URL}/api/master-panel/admin/${admin.admin_id}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to reject subscription');
      
      toast({
        title: "Rejected",
        description: `Subscription rejected for ${admin.admin_name}`,
      });
      
      fetchAdmins();
      fetchStats();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  // Revoke subscription
  const handleRevoke = async (admin) => {
    try {
      const response = await fetch(`${API_URL}/api/master-panel/admin/${admin.admin_id}/revoke`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to revoke subscription');
      
      toast({
        title: "Revoked",
        description: `Subscription revoked for ${admin.admin_name}`,
        variant: "destructive"
      });
      
      fetchAdmins();
      fetchStats();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  // Quick extend
  const handleQuickExtend = async (adminId, days) => {
    try {
      const response = await fetch(`${API_URL}/api/master-panel/admin/${adminId}/extend?days=${days}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to extend subscription');
      
      const result = await response.json();
      
      toast({
        title: "Success",
        description: result.message,
      });
      
      fetchAdmins();
      fetchStats();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  // Toggle module for admin
  const handleToggleModule = async (adminId, module, enabled) => {
    try {
      const response = await fetch(`${API_URL}/api/master-panel/admin/${adminId}/modules`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ module, enabled })
      });
      
      if (!response.ok) throw new Error('Failed to toggle module');
      
      const result = await response.json();
      
      // Update local state
      setAdmins(prev => prev.map(a => 
        a.admin_id === adminId ? { ...a, enabled_modules: result.enabledModules } : a
      ));
      
      toast({
        title: enabled ? "Module Enabled" : "Module Disabled",
        description: result.message,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  // Send notification to single user
  const handleSendNotification = async () => {
    if (!selectedAdmin || !notificationData.title.trim() || !notificationData.message.trim()) {
      toast({
        title: "Error",
        description: "Please fill in all fields",
        variant: "destructive"
      });
      return;
    }
    
    setSendingNotification(true);
    try {
      const response = await fetch(`${API_URL}/api/master-panel/notify/${selectedAdmin.admin_id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(notificationData)
      });
      
      if (!response.ok) throw new Error('Failed to send notification');
      
      toast({
        title: "Sent",
        description: `Notification sent to ${selectedAdmin.admin_name}`,
      });
      
      setNotifyDialogOpen(false);
      setNotificationData({ title: '', message: '' });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSendingNotification(false);
    }
  };

  // Broadcast notification
  const handleBroadcast = async () => {
    if (!broadcastData.title.trim() || !broadcastData.message.trim()) {
      toast({
        title: "Error",
        description: "Please fill in all fields",
        variant: "destructive"
      });
      return;
    }
    
    setSendingNotification(true);
    try {
      const response = await fetch(`${API_URL}/api/master-panel/broadcast`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(broadcastData)
      });
      
      if (!response.ok) throw new Error('Failed to send broadcast');
      
      const result = await response.json();
      
      toast({
        title: "Broadcast Sent",
        description: result.message || `Notification sent to ${result.sent_count || 'all'} users`,
      });
      
      setBroadcastDialogOpen(false);
      setBroadcastData({ title: '', message: '', target: 'all' });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSendingNotification(false);
    }
  };

  // Filter admins
  const filteredAdmins = useMemo(() => {
    let result = admins;
    
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(admin => 
        admin.admin_name?.toLowerCase().includes(term) ||
        admin.admin_email?.toLowerCase().includes(term) ||
        admin.venue_name?.toLowerCase().includes(term) ||
        admin.phone?.includes(term)
      );
    }
    
    // Status filter
    if (filterStatus !== 'all') {
      if (filterStatus === 'none') {
        result = result.filter(admin => !admin.subscription_status || admin.subscription_status === 'none');
      } else {
        result = result.filter(admin => admin.subscription_status === filterStatus);
      }
    }
    
    // Plan filter
    if (filterPlan !== 'all') {
      result = result.filter(admin => admin.subscription_plan === filterPlan);
    }
    
    // Date range filter
    if (dateRangeFilter !== 'all') {
      const now = new Date();
      let startDate, endDate;
      
      if (dateRangeFilter === 'thisMonth') {
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      } else if (dateRangeFilter === 'expiringSoon') {
        result = result.filter(admin => admin.days_remaining !== null && admin.days_remaining <= 7 && admin.days_remaining > 0);
      } else if (dateRangeFilter === 'expired') {
        result = result.filter(admin => admin.subscription_status === 'expired' || (admin.days_remaining !== null && admin.days_remaining <= 0));
      }
      
      if (startDate && endDate) {
        result = result.filter(admin => {
          if (!admin.created_at) return false;
          const createdAt = new Date(admin.created_at);
          return createdAt >= startDate && createdAt <= endDate;
        });
      }
    }
    
    return result;
  }, [admins, searchTerm, filterStatus, filterPlan, dateRangeFilter]);

  const getStatusBadge = (status, daysRemaining) => {
    if (status === 'active') {
      if (daysRemaining !== null && daysRemaining <= 7 && daysRemaining > 0) {
        return <Badge className="bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400">Expiring ({daysRemaining}d)</Badge>;
      }
      return <Badge className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">Active</Badge>;
    }
    if (status === 'expired') {
      return <Badge className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">Expired</Badge>;
    }
    if (status === 'pending') {
      return <Badge className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400">Pending</Badge>;
    }
    if (status === 'rejected') {
      return <Badge className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">Rejected</Badge>;
    }
    return <Badge className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400">None</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getPlanName = (planId) => {
    if (!planId) return 'None';
    const plan = plans.find(p => p.id === planId);
    return plan?.name || planId.replace('PLAN_', '').replace(/_/g, ' ');
  };

  if (loading && admins.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-3 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-foreground flex items-center gap-2">
            <Crown className="w-6 h-6 sm:w-8 sm:h-8 text-amber-500" />
            Master Control Panel
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-xs sm:text-sm text-gray-600 dark:text-muted-foreground">Manage all admin subscriptions</p>
            {lastUpdated && (
              <span className="text-[10px] text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full">
                Updated: {lastUpdated.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setBroadcastDialogOpen(true)} 
            size="sm"
            className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
          >
            <Megaphone className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Broadcast</span>
          </Button>
          <Button onClick={() => { fetchAdmins(); fetchStats(); }} variant="outline" size="sm">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="ml-1 hidden sm:inline">Refresh</span>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-3">
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-800">
            <CardContent className="p-2 sm:p-3">
              <div className="flex items-center gap-1 text-blue-600 mb-1">
                <Users className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="text-[10px] sm:text-xs font-medium">Total</span>
              </div>
              <p className="text-lg sm:text-2xl font-bold text-blue-700 dark:text-blue-400">{stats.total_admins}</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-800">
            <CardContent className="p-2 sm:p-3">
              <div className="flex items-center gap-1 text-green-600 mb-1">
                <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="text-[10px] sm:text-xs font-medium">Active</span>
              </div>
              <p className="text-lg sm:text-2xl font-bold text-green-700 dark:text-green-400">{stats.active_subscriptions}</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 border-yellow-200 dark:border-yellow-800">
            <CardContent className="p-2 sm:p-3">
              <div className="flex items-center gap-1 text-yellow-600 mb-1">
                <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="text-[10px] sm:text-xs font-medium">Pending</span>
              </div>
              <p className="text-lg sm:text-2xl font-bold text-yellow-700 dark:text-yellow-400">{stats.pending_subscriptions}</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 border-red-200 dark:border-red-800">
            <CardContent className="p-2 sm:p-3">
              <div className="flex items-center gap-1 text-red-600 mb-1">
                <XCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="text-[10px] sm:text-xs font-medium">Expired</span>
              </div>
              <p className="text-lg sm:text-2xl font-bold text-red-700 dark:text-red-400">{stats.expired_subscriptions}</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 border-orange-200 dark:border-orange-800">
            <CardContent className="p-2 sm:p-3">
              <div className="flex items-center gap-1 text-orange-600 mb-1">
                <AlertTriangle className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="text-[10px] sm:text-xs font-medium">Expiring</span>
              </div>
              <p className="text-lg sm:text-2xl font-bold text-orange-700 dark:text-orange-400">{stats.expiring_soon}</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-800/50 dark:to-slate-800/50 border-gray-200 dark:border-gray-700">
            <CardContent className="p-2 sm:p-3">
              <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400 mb-1">
                <Shield className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="text-[10px] sm:text-xs font-medium">No Plan</span>
              </div>
              <p className="text-lg sm:text-2xl font-bold text-gray-700 dark:text-gray-300">{stats.no_subscription}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="border-0 shadow-md">
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by name, email, venue, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {/* Filter Row */}
            <div className="flex flex-wrap items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-xs font-medium text-gray-600 dark:text-muted-foreground">Filters:</span>
              
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-28 sm:w-32 h-8 text-xs">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="none">No Plan</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterPlan} onValueChange={setFilterPlan}>
                <SelectTrigger className="w-28 sm:w-36 h-8 text-xs">
                  <SelectValue placeholder="Plan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Plans</SelectItem>
                  {plans.map(plan => (
                    <SelectItem key={plan.id} value={plan.id}>{plan.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={dateRangeFilter} onValueChange={setDateRangeFilter}>
                <SelectTrigger className="w-28 sm:w-36 h-8 text-xs">
                  <SelectValue placeholder="Date Range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="thisMonth">This Month</SelectItem>
                  <SelectItem value="expiringSoon">Expiring Soon</SelectItem>
                  <SelectItem value="expired">Expired Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Admins List */}
      <Card className="border-0 shadow-md">
        <CardHeader className="border-b dark:border-border bg-gradient-to-r from-slate-50 to-gray-50 dark:from-secondary dark:to-muted p-3 sm:p-4">
          <CardTitle className="text-base sm:text-lg dark:text-foreground">
            Admin Subscriptions ({filteredAdmins.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3 sm:p-4">
          <div className="space-y-3">
            {filteredAdmins.map((admin) => (
              <Card key={admin.admin_id} className="border">
                <CardContent className="p-3 sm:p-4">
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-3">
                    {/* Admin Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-semibold text-sm sm:text-base text-gray-900 dark:text-foreground truncate">
                          {admin.admin_name}
                        </h3>
                        {getStatusBadge(admin.subscription_status, admin.days_remaining)}
                      </div>
                      <p className="text-[11px] sm:text-xs text-gray-500 dark:text-muted-foreground truncate">{admin.admin_email}</p>
                      <p className="text-[11px] sm:text-xs text-gray-500 dark:text-muted-foreground">{admin.phone || 'N/A'}</p>
                      {admin.venue_name && (
                        <p className="text-[11px] sm:text-xs text-gray-500 dark:text-muted-foreground flex items-center gap-1">
                          <Building2 className="w-3 h-3" /> {admin.venue_name}
                        </p>
                      )}
                      
                      {/* Subscription Details */}
                      <div className="mt-2 space-y-1">
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] sm:text-xs">
                          <span className="text-gray-700 dark:text-gray-300">
                            <b>Plan:</b> {getPlanName(admin.subscription_plan)}
                          </span>
                          <span className="text-gray-700 dark:text-gray-300">
                            <b>Start:</b> {formatDate(admin.start_date)}
                          </span>
                          <span className="text-gray-700 dark:text-gray-300">
                            <b>Expiry:</b> {formatDate(admin.expiry_date)}
                            {admin.days_remaining !== null && admin.days_remaining > 0 && (
                              <span className={`ml-1 ${admin.days_remaining <= 7 ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                                ({admin.days_remaining}d left)
                              </span>
                            )}
                          </span>
                        </div>
                        {admin.payment_method && (
                          <div className="flex items-center gap-1 text-[11px] sm:text-xs text-gray-600 dark:text-muted-foreground">
                            <Wallet className="w-3 h-3" />
                            <span className="capitalize">{admin.payment_method.replace(/_/g, ' ')}</span>
                          </div>
                        )}
                      </div>

                      {/* Module Toggle */}
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-800">
                          <Receipt className="w-3 h-3 text-teal-600 dark:text-teal-400" />
                          <span className="text-[10px] sm:text-xs text-gray-700 dark:text-gray-300">Invoices</span>
                          <Switch
                            data-testid={`toggle-invoices-${admin.admin_id}`}
                            checked={(admin.enabled_modules || ['invoices']).includes('invoices')}
                            onCheckedChange={(checked) => handleToggleModule(admin.admin_id, 'invoices', checked)}
                            className="scale-75"
                          />
                        </div>
                        <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-800">
                          <FileText className="w-3 h-3 text-indigo-600 dark:text-indigo-400" />
                          <span className="text-[10px] sm:text-xs text-gray-700 dark:text-gray-300">Quotations</span>
                          <Switch
                            data-testid={`toggle-quotations-${admin.admin_id}`}
                            checked={(admin.enabled_modules || []).includes('quotations')}
                            onCheckedChange={(checked) => handleToggleModule(admin.admin_id, 'quotations', checked)}
                            className="scale-75"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-1.5 sm:gap-2 w-full lg:w-auto">
                      {/* Notify Button */}
                      <Button
                        onClick={() => {
                          setSelectedAdmin(admin);
                          setNotificationData({ title: '', message: '' });
                          setNotifyDialogOpen(true);
                        }}
                        size="sm"
                        variant="outline"
                        className="h-7 sm:h-8 px-2 sm:px-3 text-[10px] sm:text-xs border-orange-300 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20"
                      >
                        <Bell className="w-3 h-3 mr-1" />
                        Notify
                      </Button>

                      {/* Status-based actions */}
                      {admin.subscription_status === 'pending' && (
                        <>
                          <Button
                            onClick={() => handleApprove(admin)}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 h-7 sm:h-8 px-2 sm:px-3 text-[10px] sm:text-xs"
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Approve
                          </Button>
                          <Button
                            onClick={() => handleReject(admin)}
                            size="sm"
                            variant="destructive"
                            className="h-7 sm:h-8 px-2 sm:px-3 text-[10px] sm:text-xs"
                          >
                            <XCircle className="w-3 h-3 mr-1" />
                            Reject
                          </Button>
                        </>
                      )}

                      {admin.subscription_status === 'active' && (
                        <>
                          <Button
                            onClick={() => handleQuickExtend(admin.admin_id, 30)}
                            size="sm"
                            variant="outline"
                            className="h-7 sm:h-8 px-2 sm:px-3 text-[10px] sm:text-xs text-green-600 border-green-300 hover:bg-green-50 dark:hover:bg-green-900/20"
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            +30d
                          </Button>
                          <Button
                            onClick={() => handleRevoke(admin)}
                            size="sm"
                            variant="destructive"
                            className="h-7 sm:h-8 px-2 sm:px-3 text-[10px] sm:text-xs"
                          >
                            <Ban className="w-3 h-3 mr-1" />
                            Revoke
                          </Button>
                        </>
                      )}

                      {(admin.subscription_status === 'expired' || admin.subscription_status === 'rejected') && (
                        <Button
                          onClick={() => handleApprove(admin)}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 h-7 sm:h-8 px-2 sm:px-3 text-[10px] sm:text-xs"
                        >
                          <RotateCcw className="w-3 h-3 mr-1" />
                          Reactivate
                        </Button>
                      )}

                      {/* Edit Subscription */}
                      <Button
                        onClick={() => handleEditClick(admin)}
                        size="sm"
                        variant="outline"
                        className="h-7 sm:h-8 px-2 sm:px-3 text-[10px] sm:text-xs"
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Edit
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {filteredAdmins.length === 0 && (
              <div className="text-center py-8 text-gray-500 dark:text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>No admins found matching your criteria</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Edit Subscription Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 dark:text-foreground">
              <CreditCard className="w-5 h-5 text-teal-600" />
              Edit Subscription
            </DialogTitle>
            <DialogDescription>
              Manage subscription for {selectedAdmin?.admin_name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedAdmin && (
            <div className="space-y-4 py-4">
              {/* Admin Info */}
              <div className="bg-gray-50 dark:bg-secondary p-3 rounded-lg">
                <p className="font-semibold text-gray-900 dark:text-foreground">{selectedAdmin.admin_name}</p>
                <p className="text-sm text-gray-600 dark:text-muted-foreground">{selectedAdmin.admin_email}</p>
                <p className="text-sm text-gray-600 dark:text-muted-foreground">{selectedAdmin.venue_name}</p>
              </div>

              {/* Plan Selection */}
              <div className="space-y-2">
                <Label>Subscription Plan</Label>
                <Select value={editData.plan} onValueChange={(v) => setEditData({...editData, plan: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select plan" />
                  </SelectTrigger>
                  <SelectContent>
                    {plans.map(plan => (
                      <SelectItem key={plan.id} value={plan.id}>{plan.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Status */}
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={editData.status} onValueChange={(v) => setEditData({...editData, status: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={editData.start_date}
                    onChange={(e) => setEditData({...editData, start_date: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Expiry Date</Label>
                  <Input
                    type="date"
                    value={editData.expiry_date}
                    onChange={(e) => setEditData({...editData, expiry_date: e.target.value})}
                  />
                </div>
              </div>

              {/* Payment Mode */}
              <div className="space-y-2">
                <Label>Payment Mode</Label>
                <Select value={editData.payment_mode} onValueChange={(v) => setEditData({...editData, payment_mode: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select payment mode" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free_trial">Free Trial</SelectItem>
                    <SelectItem value="online">Online Payment</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="master_approved">Master Approved</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Extend Days */}
              <div className="space-y-2">
                <Label>Extend Subscription (days)</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    min="0"
                    value={editData.extend_days}
                    onChange={(e) => setEditData({...editData, extend_days: parseInt(e.target.value) || 0})}
                    placeholder="0"
                    className="flex-1"
                  />
                  <div className="flex gap-1">
                    <Button type="button" variant="outline" size="sm" onClick={() => setEditData({...editData, extend_days: 30})}>
                      +30
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => setEditData({...editData, extend_days: 90})}>
                      +90
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => setEditData({...editData, extend_days: 365})}>
                      +1Y
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveSubscription} disabled={saving}>
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notify Single User Dialog */}
      <Dialog open={notifyDialogOpen} onOpenChange={setNotifyDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 dark:text-foreground">
              <Bell className="w-5 h-5 text-orange-500" />
              Send Notification
            </DialogTitle>
            <DialogDescription>
              Send a notification to {selectedAdmin?.admin_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                placeholder="Notification title..."
                value={notificationData.title}
                onChange={(e) => setNotificationData({...notificationData, title: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea
                placeholder="Enter your message..."
                value={notificationData.message}
                onChange={(e) => setNotificationData({...notificationData, message: e.target.value})}
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setNotifyDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendNotification} disabled={sendingNotification}>
              {sendingNotification ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Broadcast Dialog */}
      <Dialog open={broadcastDialogOpen} onOpenChange={setBroadcastDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 dark:text-foreground">
              <Megaphone className="w-5 h-5 text-red-500" />
              Broadcast Message
            </DialogTitle>
            <DialogDescription>
              Send a notification to multiple admins
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Target Audience</Label>
              <Select value={broadcastData.target} onValueChange={(v) => setBroadcastData({...broadcastData, target: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select target" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Admins</SelectItem>
                  <SelectItem value="active">Active Subscriptions Only</SelectItem>
                  <SelectItem value="expiring">Expiring Soon (7 days)</SelectItem>
                  <SelectItem value="expired">Expired Only</SelectItem>
                  <SelectItem value="pending">Pending Approval</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                placeholder="Broadcast title..."
                value={broadcastData.title}
                onChange={(e) => setBroadcastData({...broadcastData, title: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea
                placeholder="Enter your broadcast message..."
                value={broadcastData.message}
                onChange={(e) => setBroadcastData({...broadcastData, message: e.target.value})}
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBroadcastDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBroadcast} 
              disabled={sendingNotification}
              className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
            >
              {sendingNotification ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Megaphone className="w-4 h-4 mr-2" />
                  Broadcast
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MasterControlPanel;
